import React, { useMemo, useState } from "react";
import { Copy, Download, ShoppingBasket, Check } from "lucide-react";
import { toast } from "sonner";
import { Modal } from "./Modal";
import { useI18n } from "../i18n/LanguageContext";

const UNITS = /^(kg|g|ml|l|litros?|liters?|litres?|litri|colheres?|colher|cucharadas?|cucharaditas?|cuillères?|cucchiai(?:o|ni)?|c\.|tbsp|tsp|cups?|xícaras?|chávenas?|tazas?|tasses?|tazze|dentes?|cloves?|dientes?|gousses?|spicchi|ramos?|maços?|fatias?|slices?|unidades?|latas?|pacotes?|potes?|ovos?|eggs?|huevos?|œufs?|uova)\b/i;

const parseQty = (s) => {
  const m = s.match(/^(\d+\s+\d+\/\d+|\d+\/\d+|\d+(?:[.,]\d+)?)\s*(.*)$/);
  if (!m) return null;
  const frac = (f) => Number(f.split("/")[0]) / Number(f.split("/")[1]);
  const parts = m[1].trim().split(/\s+/);
  const qty = parts.reduce((a, p) => a + (p.includes("/") ? frac(p) : Number(p.replace(",", "."))), 0);
  return { qty, rest: m[2] };
};

const fmtQty = (q, hasUnit) => {
  if (q >= 10) return String(Math.round(q));
  const r = hasUnit ? Math.round(q * 4) / 4 : Math.ceil(q - 0.05);
  return String(Math.max(r, hasUnit ? 0.25 : 1)).replace(".", ",");
};

// Scale every ingredient of the given recipes to `guests`, then merge identical ingredient names.
export const buildMenuList = (recipes, guests) => {
  const map = new Map();
  const plain = [];
  recipes.forEach((r) => {
    const factor = r.servings ? Number(guests) / Number(r.servings) : 1;
    (r.ingredients || []).forEach((ing) => {
      const p = parseQty(ing);
      if (!p) { if (!plain.some((x) => x.toLowerCase() === ing.toLowerCase())) plain.push(ing); return; }
      const key = p.rest.toLowerCase().trim();
      const cur = map.get(key) || { qty: 0, rest: p.rest };
      cur.qty += p.qty * factor;
      map.set(key, cur);
    });
  });
  return [...[...map.values()].map((x) => `${fmtQty(x.qty, UNITS.test(x.rest))} ${x.rest}`), ...plain];
};

export function MenuShoppingList({ open, onClose, title, recipes, guests }) {
  const { t } = useI18n();
  const items = useMemo(() => buildMenuList(recipes, guests), [recipes, guests]);
  const [done, setDone] = useState({});
  const text = `🛒 *${t("ceia.list_title")} — ${title}*\n${t("ceia.list_for", { n: guests, k: recipes.length })}\n\n${items.map((i) => `• ${i}`).join("\n")}\n\n${t("ceia.list_note")}`;
  const copy = () => navigator.clipboard.writeText(text).then(() => toast.success(t("ceia.list_copied")));
  const download = () => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([text.replace(/\*/g, "")], { type: "text/plain;charset=utf-8" }));
    a.download = "lista-de-compras.txt";
    a.click();
  };
  return (
    <Modal open={open} onClose={onClose} title={`${t("ceia.list_title")} · ${title}`} testid="ceia-menu-list-modal">
      <p className="text-sm text-xmas-slate" data-testid="ceia-menu-list-for">{t("ceia.list_for", { n: guests, k: recipes.length })}</p>
      <ul className="mt-4 max-h-[50vh] space-y-1.5 overflow-y-auto pr-1" data-testid="ceia-menu-list-items">
        {items.map((it, i) => (
          <li key={i}>
            <button onClick={() => setDone((d) => ({ ...d, [i]: !d[i] }))} data-testid={`ceia-menu-list-item-${i}`}
              className={`flex w-full items-center gap-3 rounded-lg border px-3 py-2 text-left text-sm transition ${done[i] ? "border-xmas-gold/40 bg-xmas-gold/10 text-xmas-slate line-through" : "border-xmas-gold/15 text-xmas-snow hover:border-xmas-gold/40"}`}>
              <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${done[i] ? "border-xmas-gold bg-xmas-gold text-xmas-navy" : "border-xmas-gold/40"}`}>{done[i] && <Check className="h-3 w-3" />}</span>
              {it}
            </button>
          </li>
        ))}
      </ul>
      <p className="mt-3 text-[11px] text-xmas-slate/80">{t("ceia.list_note")}</p>
      <div className="mt-4 flex flex-wrap gap-2">
        <button onClick={copy} data-testid="ceia-menu-list-copy" className="glass inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold text-xmas-snow hover:text-xmas-gold"><Copy className="h-3.5 w-3.5" /> {t("ceia.list_copy")}</button>
        <a href={`https://wa.me/?text=${encodeURIComponent(text)}`} target="_blank" rel="noreferrer" data-testid="ceia-menu-list-whatsapp" className="inline-flex items-center gap-1.5 rounded-full bg-[#25D366] px-4 py-2 text-xs font-bold text-white hover:brightness-110"><ShoppingBasket className="h-3.5 w-3.5" /> {t("ceia.list_whatsapp")}</a>
        <button onClick={download} data-testid="ceia-menu-list-download" className="glass inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold text-xmas-snow hover:text-xmas-gold"><Download className="h-3.5 w-3.5" /> {t("ceia.list_download")}</button>
      </div>
    </Modal>
  );
}
